/**
 * ★ THE INBOX (design 24, T17 part A; the owner's pick A1, 2026-10-11) - one sidebar entry and one
 * page for what readers send the author, and the way to the MoonX team.
 *
 * WHAT IT REPLACED. Two sidebar entries, "Comments" (what readers said under the author's
 * articles) and "Messages" (what readers wrote to the author, with a tip or without). Both are
 * readers talking to the author, so they are two tabs of one page now, with a third, "MoonX team",
 * where a chat with the team will go once it is built - its own session, which starts by reopening
 * D37. Until then that tab says how to reach the team by e-mail (./TeamTab.tsx).
 *
 * THE TWO SCREENS ARE DRAWN WHOLE INSIDE THEIR TABS, unchanged (routes/comments/CommentsRoute.tsx,
 * routes/messages/MessagesRoute.tsx), and each one's heading is the page's one `<h1>`: the tabs sit
 * above it, and only the chosen tab is drawn (`keepMounted={false}`), so only it reads.
 *
 * THE OLD ADDRESSES OPEN THEIR TABS. `/comments` and `/messages` are still routes, drawing this page
 * on that tab, so the links the Overview, the statistics and old notifications carry keep working,
 * and the sidebar lights "Inbox" on them (features/cabinet/routes.ts, `SIDEBAR_PARENTS`). A tab
 * chosen here is the page's own state, as Statistics' tabs are: the address does not follow it.
 *
 * NOTIFICATIONS ARE A TAB TOO (v1.0.14, the owner, 15 October 2026: "move them into inbox"). The
 * screen is routes/notifications/NotificationsRoute.tsx, drawn whole like the other two, and its old
 * address `/notifications` opens this page on that tab - the monthly statement and other e-mails
 * link there.
 */
import { Tabs } from '@mantine/core';
import { useLocation } from '@tanstack/react-router';
import { useState } from 'react';
import { CommentsRoute } from '../comments/CommentsRoute';
import { commentsArticleOf } from '../comments/comments-href';
import { MessagesRoute } from '../messages';
import { NotificationsRoute } from '../notifications/NotificationsRoute';
import { TeamTab } from './TeamTab';
import { useTranslation } from '../../i18n';
import classes from './inbox.module.css';

export type InboxTab = 'comments' | 'messages' | 'notifications' | 'team';

/** The tabs in their order; each one's name is `inbox.tabs.<tab>` in the catalogue. */
const TABS: readonly InboxTab[] = ['comments', 'messages', 'notifications', 'team'];

function isInboxTab(value: string | null): value is InboxTab {
  return TABS.some((tab) => tab === value);
}

export interface InboxRouteProps {
  /** The tab the page opens on. The router mounts `/inbox` with no props: Comments. */
  readonly tab?: InboxTab;
  /** Comments on this one article only - `/comments?article=<id>` (../comments/comments-href.ts). */
  readonly commentsArticleId?: string | undefined;
}

export function InboxRoute({ tab = 'comments', commentsArticleId }: InboxRouteProps) {
  const { t } = useTranslation('engagement');
  const [shown, setShown] = useState<InboxTab>(tab);

  return (
    <Tabs
      value={shown}
      onChange={(next) => {
        if (isInboxTab(next)) {
          setShown(next);
        }
      }}
      keepMounted={false}
      unstyled
      classNames={{ list: classes.tabList, tab: classes.tab, panel: classes.panel }}
    >
      <Tabs.List aria-label={t('inbox.label')}>
        {TABS.map((value) => (
          <Tabs.Tab key={value} value={value}>
            {t(`inbox.tabs.${value}`)}
          </Tabs.Tab>
        ))}
      </Tabs.List>
      <Tabs.Panel value="comments">
        <CommentsRoute
          {...(commentsArticleId === undefined ? {} : { articleId: commentsArticleId })}
        />
      </Tabs.Panel>
      <Tabs.Panel value="messages">
        <MessagesRoute />
      </Tabs.Panel>
      <Tabs.Panel value="notifications">
        <NotificationsRoute />
      </Tabs.Panel>
      <Tabs.Panel value="team">
        <TeamTab />
      </Tabs.Panel>
    </Tabs>
  );
}

/**
 * `/comments`, the old Comments screen's address: the Inbox on its Comments tab - one article's
 * comments when the address names it (`?article=`, an article's own page links there).
 */
export function CommentsInboxRoute() {
  const articleId = useLocation({ select: (location) => commentsArticleOf(location.searchStr) });
  // Keyed on it, so moving between one article and all of them starts the list afresh.
  return <InboxRoute key={articleId ?? 'all'} tab="comments" commentsArticleId={articleId} />;
}

/** `/messages`, the old Messages screen's address: the Inbox on its Messages tab. */
export function MessagesInboxRoute() {
  return <InboxRoute tab="messages" />;
}

export function NotificationsInboxRoute() {
  return <InboxRoute tab="notifications" />;
}
